import React, { Fragment } from 'react';
import classes from './UserCard.module.scss';
import { SettingsTeamMember } from '../../../models/settings-pages';
import { UserOrganizationRole } from '../../../models/graphql';
import { GetOrganization_organization_workspaces } from '../../../models/legacy-graphql';
import { RegisterOptions, useForm, UseFormReturn } from 'react-hook-form';
import { Toggle } from '../../../components/forms/Toggle';
import { Panel } from '../../../components/containers/Panel';
import { Select } from '../../../components/forms/Select';
import { FormOptions } from '../../../constants/form';
import { RemoveTeamMemberModal } from '../RemoveTeamMemberModal';
import { ContainerListItem } from '../../../components/actions/ListItem/ListItem.view';
import { PanelHeader } from '../../../components/containers/Panel/PanelHeader.view';
import classNames from 'classnames';
import { SelectItem } from '../../../components/forms/Select/Select.view';
import { ButtonMenu } from '../../../components/actions/ButtonMenu';
import { Debugger } from '../../../components/containers/Debugger';

export type UserCardViewProps = {
  user: SettingsTeamMember;
  workspaces: GetOrganization_organization_workspaces[] | undefined;
  assignedWorkspaceIds: string[];
  isSelf: boolean;
  isInvitedUser: boolean;
  selectedOrgRole: UserOrganizationRole | null;
  isDotMenuEnabled: boolean;
  removeUserMenuLabel: string;
  openModal: boolean;
  setOpenModal: (openRemoveModal: boolean) => void;
  userRoleChangeHandler: (role: UserOrganizationRole, complete: (keepNewVal: boolean | undefined) => void) => void;
  resendUserInvitationHandler: (invitationId: string) => void;
  workspaceAssignmentToggleAction: (
    newVal: boolean,
    workspaceId: string,
    complete: (keepNewVal: boolean | undefined) => void
  ) => void;
  rules: RegisterOptions;
};

export const UserCardView = (props: UserCardViewProps): JSX.Element => {
  const useFormInstance: UseFormReturn = useForm(FormOptions);
  const workspaceCount = props.workspaces?.length || 0;

  const workspaceToggleComponents = props.workspaces?.map((workspace, i) => {
    const isSelectedWorkspace = props.assignedWorkspaceIds.includes(workspace.id);
    const isLastSelectedWorkspace = isSelectedWorkspace && props.assignedWorkspaceIds.length < 2;

    const toggleWrapperClasses = classNames('margin-top-small-8', {
      'margin-bottom-small-8': workspaceCount > i + 1
    });

    return (
      <Fragment key={`workspace-${workspace.id}`}>
        <div className={toggleWrapperClasses}>
          <Toggle
            name={`toggle-workspace-${workspace.id}`}
            formInstance={useFormInstance}
            rules={props.rules}
            label={workspace.name}
            checked={isSelectedWorkspace}
            disabled={isLastSelectedWorkspace}
            action={(value, complete) => {
              props.workspaceAssignmentToggleAction(value, workspace.id, complete);
            }}
          />
        </div>
      </Fragment>
    );
  });

  const RoleSelect = (user: SettingsTeamMember) => {
    const selectedRole = user.role;
    const selectOptions: SelectItem[] = [];

    if (selectedRole === UserOrganizationRole.OWNER) {
      selectOptions.push({
        label: 'Owner',
        value: UserOrganizationRole.OWNER
      });
    } else {
      selectOptions.push(
        {
          label: 'Editor',
          value: UserOrganizationRole.EDITOR
        },
        {
          label: `Admin`,
          value: UserOrganizationRole.ADMIN
        }
      );
    }

    const isSelectDisabled =
      // eslint-disable-next-line react/prop-types
      props.selectedOrgRole !== UserOrganizationRole.OWNER || selectedRole === UserOrganizationRole.OWNER;

    return (
      <Select
        items={selectOptions}
        label={'Role'}
        name={'role'}
        useFormInstance={useFormInstance}
        size={'large'}
        action={(role, complete) => {
          // eslint-disable-next-line react/prop-types
          props.userRoleChangeHandler(role as UserOrganizationRole, complete);
        }}
        selectedValue={selectedRole}
        rules={{}}
        readonly={isSelectDisabled}
      />
    );
  };

  let listItems: ContainerListItem[] = [
    {
      action: () => {
        props.setOpenModal(true);
      },
      label: props.removeUserMenuLabel
    }
  ];

  listItems = props.isInvitedUser
    ? [
        ...listItems,
        {
          action: () => {
            props.resendUserInvitationHandler(props.user.id);
          },
          label: 'Resend Invite'
        }
      ]
    : listItems;

  const meBadgeClasses = classNames(classes.Pill, classes[`PillMe`]);
  const invitedBadgeClasses = classNames(classes.Pill, classes[`PillInvited`]);

  const titleBadge = props.isSelf ? (
    <span className={meBadgeClasses}>Me</span>
  ) : props.isInvitedUser ? (
    <span className={invitedBadgeClasses}>Invited</span>
  ) : null;

  const dotMenu = props.isDotMenuEnabled ? (
    <ButtonMenu isSquareButton={true} buttonType={'transparent'} side={'right'} listItems={listItems} />
  ) : null;

  const debuggerModalContent =
    props.isInvitedUser && props.user.invitationToken
      ? { invitationUrl: window.location.origin + '/invite/' + props.user.invitationToken, user: props.user }
      : props.user;

  return (
    <Fragment>
      <Panel>
        <PanelHeader
          title={props.user.name}
          titleBadge={titleBadge}
          subTitle={props.user.email}
          squareButtonMenu={dotMenu}
        />
        <div className={'grid-x'}>
          <Debugger
            tooltipContent={(!props.isInvitedUser ? 'User ID: ' : 'Invitation ID: ') + props.user.id}
            modalContent={debuggerModalContent}
          />
          <div className={'cell small-12 large-6 margin-bottom-small-32'}>
            <div className={'cell small-12 large-6'}>{RoleSelect(props.user)}</div>
          </div>
        </div>
        <div className={'grid-x'}>
          <div className={'cell small-12 margin-bottom-small-32'}>
            <h6 className={'uppercase margin-bottom-small-16'}>Workspace Permissions</h6>
            {props.user.role === UserOrganizationRole.OWNER || props.user.role === UserOrganizationRole.ADMIN ? (
              <div className={classes.AllWorkspaces}>All Workspaces</div>
            ) : (
              <div className={'grid-y'}>{workspaceToggleComponents}</div>
            )}
          </div>
        </div>
      </Panel>
      {props.openModal && <RemoveTeamMemberModal user={props.user} onClose={() => props.setOpenModal(false)} />}
    </Fragment>
  );
};
